import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox,
  ListItemText,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useSalonsMySalonsRetrieveQuery,
  useSalonClosuresListQuery,
  useSalonClosuresCreateMutation,
  useSalonClosureUpdateMutation,
  useSalonClosureDeleteMutation,
} from '../../../services/salonsApi';

import { theme } from '../../../theme';

const isFullDayDateRange = (start, end) => {
  if (!start || !end) return false;
  return (
    start.getUTCHours() === 0 &&
    start.getUTCMinutes() === 0 &&
    end.getUTCHours() === 23 &&
    end.getUTCMinutes() === 59
  );
};

const formatUtcTime = (date) => {
  if (!date) return '00:00';
  const h = String(date.getUTCHours()).padStart(2, '0');
  const m = String(date.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

const isRecurringFullDay = (closure) => {
  const st = closure?.start_time || '00:00:00';
  const et = closure?.end_time || '23:59:59';
  return st.startsWith('00:00') && et.startsWith('23:59');
};

const formatDisplay = (closure, t) => {
  if (!closure) return '';

  if (closure.is_recurring) {
    const month = closure.month;
    const day = closure.day;
    const st = closure.start_time || '00:00:00';
    const et = closure.end_time || '23:59:59';
    const timeLabel = isRecurringFullDay(closure) ? t('pro_dashboard.salon.all_day') : `${st.slice(0, 5)} - ${et.slice(0, 5)}`;
    return `${t('pro_dashboard.salon.every_year')} ${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')} • ${timeLabel}`;
  }

  const start = closure.start_datetime ? new Date(closure.start_datetime) : null;
  const end = closure.end_datetime ? new Date(closure.end_datetime) : null;
  if (!start || !end) return t('pro_dashboard.salon.one_time_closure') || '';

  const date = start.toLocaleDateString('fr-FR');
  const timeLabel = isFullDayDateRange(start, end)
    ? t('pro_dashboard.salon.all_day')
    : `${start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
  return `${date} • ${timeLabel}`;
};

const SalonClosures = ({ initialSalonId: initialSalonIdProp = null, embedded = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: mySalons, isLoading: salonsLoading, error: salonsError } = useSalonsMySalonsRetrieveQuery();

  const salons = useMemo(() => (Array.isArray(mySalons) ? mySalons : []), [mySalons]);

  const initialSalonId = useMemo(() => {
    if (initialSalonIdProp) return Number(initialSalonIdProp);
    const fromState = location?.state?.salonId;
    if (fromState) return Number(fromState);
    const first = salons[0]?.salon_id;
    return first ? Number(first) : null;
  }, [initialSalonIdProp, location?.state?.salonId, salons]);

  const effectiveSalonId = initialSalonId;

  const {
    data: closures,
    isLoading: closuresLoading,
    error: closuresError,
    refetch,
  } = useSalonClosuresListQuery(
    { salonId: effectiveSalonId },
    { skip: !effectiveSalonId, refetchOnMountOrArgChange: true }
  );

  const [createClosure, { isLoading: creating }] = useSalonClosuresCreateMutation();
  const [updateClosure, { isLoading: updating }] = useSalonClosureUpdateMutation();
  const [deleteClosure, { isLoading: deleting }] = useSalonClosureDeleteMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [repeatMode, setRepeatMode] = useState('once');
  const [repeatUntil, setRepeatUntil] = useState('');
  const [fullDay, setFullDay] = useState(true);
  const [reason, setReason] = useState('');
  const [editingClosure, setEditingClosure] = useState(null);

  const [applyScope, setApplyScope] = useState('single');
  const [applySalonIds, setApplySalonIds] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [closureToDelete, setClosureToDelete] = useState(null);

  // one-time
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('13:00');

  useEffect(() => {
    if (repeatMode === 'once' || repeatMode === 'yearly') {
      return;
    }

    // default: end of current year
    if (!repeatUntil) {
      const y = new Date().getFullYear();
      setRepeatUntil(`${y}-12-31`);
    }
  }, [repeatMode, repeatUntil]);

  const isEditing = Boolean(editingClosure);

  const openCreate = () => {
    const todayYmd = new Date().toISOString().slice(0, 10);
    setEditingClosure(null);
    setApplyScope('single');
    setApplySalonIds([]);
    setRepeatMode('once');
    setRepeatUntil('');
    setFullDay(true);
    setReason('');
    setDate(todayYmd);
    setDialogOpen(true);
  };

  const closeCreate = () => {
    setDialogOpen(false);
    setEditingClosure(null);
  };

  const openEdit = (closure) => {
    if (!closure) return;

    setEditingClosure(closure);
    setApplyScope('single');
    setApplySalonIds([]);
    setRepeatUntil('');
    setReason(closure.reason || '');

    if (closure.is_recurring) {
      setRepeatMode('yearly');
      const nowYear = new Date().getFullYear();
      const month = String(closure.month || 1).padStart(2, '0');
      const day = String(closure.day || 1).padStart(2, '0');
      setDate(`${nowYear}-${month}-${day}`);
      const full = isRecurringFullDay(closure);
      setFullDay(full);
      setStartTime((closure.start_time || '00:00').slice(0, 5));
      setEndTime((closure.end_time || '23:59').slice(0, 5));
    } else {
      setRepeatMode('once');
      const start = closure.start_datetime ? new Date(closure.start_datetime) : null;
      const end = closure.end_datetime ? new Date(closure.end_datetime) : null;
      setDate(start ? start.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
      setStartTime(start ? formatUtcTime(start) : '08:00');
      setEndTime(end ? formatUtcTime(end) : '18:00');
      setFullDay(isFullDayDateRange(start, end));
    }

    setDialogOpen(true);
  };

  const buildPayloads = () => {
    const toIso = (ymd, time) => new Date(`${ymd}T${time}Z`).toISOString();
    const makeOneTime = (ymd) => ({
      is_recurring: false,
      start_datetime: toIso(ymd, fullDay ? '00:00:00' : `${startTime}:00`),
      end_datetime: toIso(ymd, fullDay ? '23:59:59' : `${endTime}:00`),
      reason: reason || undefined,
      status: 'active',
    });

    if (!date) {
      throw new Error(t('pro_dashboard.salon.select_date'));
    }

    if (repeatMode === 'yearly') {
      const base = new Date(`${date}T00:00:00Z`);
      const m = base.getUTCMonth() + 1;
      const d = base.getUTCDate();
      const payload = {
        is_recurring: true,
        month: m,
        day: d,
        reason: reason || undefined,
        status: 'active',
        start_time: fullDay ? '00:00:00' : `${startTime}:00`,
        end_time: fullDay ? '23:59:59' : `${endTime}:00`,
      };
      return [payload];
    }

    if (repeatMode === 'once') {
      return [makeOneTime(date)];
    }

    if (!repeatUntil) {
      throw new Error(t('pro_dashboard.salon.select_end_date'));
    }

    const start = new Date(`${date}T00:00:00Z`);
    const until = new Date(`${repeatUntil}T00:00:00Z`);
    if (until < start) {
      throw new Error(t('pro_dashboard.salon.end_date_after_start'));
    }

    const results = [];
    const max = 400;

    const addDays = (dt, n) => {
      const d = new Date(dt);
      d.setUTCDate(d.getUTCDate() + n);
      return d;
    };

    const addMonthsClamped = (dt, n) => {
      const y = dt.getUTCFullYear();
      const m = dt.getUTCMonth();
      const day = dt.getUTCDate();

      const targetMonthIndex = m + n;
      const targetY = y + Math.floor(targetMonthIndex / 12);
      const targetM = ((targetMonthIndex % 12) + 12) % 12;
      const daysInTargetMonth = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
      const clampedDay = Math.min(day, daysInTargetMonth);
      return new Date(Date.UTC(targetY, targetM, clampedDay));
    };

    for (let cursor = new Date(start); cursor <= until; ) {
      const wd = cursor.getUTCDay(); // 0 Sun .. 6 Sat
      const isWeekend = wd === 0 || wd === 6;
      const shouldInclude = repeatMode === 'weekdays' ? !isWeekend : true;

      if (shouldInclude) {
        const ymd = cursor.toISOString().slice(0, 10);
        results.push(makeOneTime(ymd));
        if (results.length > max) {
          throw new Error(t('pro_dashboard.salon.too_many_occurrences'));
        }
      }

      if (repeatMode === 'daily' || repeatMode === 'weekdays') {
        cursor = addDays(cursor, 1);
      } else if (repeatMode === 'weekly') {
        cursor = addDays(cursor, 7);
      } else if (repeatMode === 'monthly') {
        cursor = addMonthsClamped(cursor, 1);
      } else {
        break;
      }
    }

    return results;
  };

  const buildEditPayload = () => {
    const toIso = (ymd, time) => new Date(`${ymd}T${time}Z`).toISOString();
    if (!date) {
      throw new Error(t('pro_dashboard.salon.select_date'));
    }

    if (repeatMode === 'yearly') {
      const base = new Date(`${date}T00:00:00Z`);
      return {
        is_recurring: true,
        month: base.getUTCMonth() + 1,
        day: base.getUTCDate(),
        start_time: fullDay ? '00:00:00' : `${startTime}:00`,
        end_time: fullDay ? '23:59:59' : `${endTime}:00`,
        reason: reason || undefined,
      };
    }

    if (repeatMode !== 'once') {
      throw new Error(t('pro_dashboard.salon.repetition_not_modifiable'));
    }

    return {
      is_recurring: false,
      start_datetime: toIso(date, fullDay ? '00:00:00' : `${startTime}:00`),
      end_datetime: toIso(date, fullDay ? '23:59:59' : `${endTime}:00`),
      reason: reason || undefined,
    };
  };

  const handleCreate = async () => {
    if (!effectiveSalonId) return;

    if (isEditing && editingClosure) {
      try {
        const payload = buildEditPayload();
        await updateClosure({
          salonId: effectiveSalonId,
          closureId: editingClosure.closure_id,
          body: payload,
        }).unwrap();
        closeCreate();
        await refetch();
        setSnackbar({ open: true, message: t('pro_dashboard.salon.closure_update_success'), severity: 'success' });
      } catch (e) {
        console.error('Update closure failed', e);
        setSnackbar({ open: true, message: t('pro_dashboard.salon.closure_update_error'), severity: 'error' });
      }
      return;
    }

    const targetSalonIds = (() => {
      if (applyScope === 'all') return salons.map((s) => Number(s.salon_id)).filter(Boolean);
      if (applyScope === 'list') return (applySalonIds || []).map((x) => Number(x)).filter(Boolean);
      return [Number(effectiveSalonId)];
    })();

    if (targetSalonIds.length === 0) {
      setSnackbar({ open: true, message: t('pro_dashboard.salon.no_salon_selected'), severity: 'error' });
      return;
    }

    try {
      const payloads = buildPayloads();
      for (const sid of targetSalonIds) {
        for (const body of payloads) {
          await createClosure({ salonId: sid, body }).unwrap();
        }
      }
      closeCreate();
      setReason('');
      setApplyScope('single');
      setApplySalonIds([]);
      await refetch();
      setSnackbar({ open: true, message: t('pro_dashboard.salon.closure_creation_success'), severity: 'success' });
    } catch (e) {
      // keep it simple
      console.error('Create closure failed', e);
      setSnackbar({ open: true, message: t('pro_dashboard.salon.closure_creation_error'), severity: 'error' });
    }
  };

  const handleDelete = async (closureId) => {
    if (!effectiveSalonId) return;
    setClosureToDelete(closureId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!effectiveSalonId || !closureToDelete) return;

    try {
      await deleteClosure({ salonId: effectiveSalonId, closureId: closureToDelete }).unwrap();
      await refetch();
      setDeleteDialogOpen(false);
      setClosureToDelete(null);
      setSnackbar({ open: true, message: t('pro_dashboard.salon.closure_delete_success'), severity: 'success' });
    } catch (e) {
      console.error('Delete closure failed', e);
      setDeleteDialogOpen(false);
      setClosureToDelete(null);
      setSnackbar({ open: true, message: t('pro_dashboard.salon.closure_delete_error'), severity: 'error' });
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setClosureToDelete(null);
  };

  const isLoading = salonsLoading || closuresLoading;

  const closureRecap = useMemo(() => {
    if (!date) return t('pro_dashboard.salon.select_date_short');

    const base = new Date(`${date}T00:00:00Z`);
    const weekday = base.toLocaleDateString('fr-FR', { weekday: 'long' });
    const monthName = base.toLocaleDateString('fr-FR', { month: 'long' });
    const dayNum = base.getUTCDate();

    const timeLabel = fullDay ? t('pro_dashboard.salon.all_day') : `${startTime} - ${endTime}`;
    const untilLabel = repeatUntil ? new Date(`${repeatUntil}T00:00:00Z`).toLocaleDateString('fr-FR') : '';

    if (repeatMode === 'once') {
      return `${base.toLocaleDateString('fr-FR')} - ${timeLabel}`;
    }
    if (repeatMode === 'daily') {
      return `${t('pro_dashboard.salon.every_day')} • ${timeLabel}${untilLabel ? ` • ${t('pro_dashboard.salon.until')} ${untilLabel}` : ''}`;
    }
    if (repeatMode === 'weekdays') {
      return `${t('pro_dashboard.salon.every_weekday')} • ${timeLabel}${untilLabel ? ` • ${t('pro_dashboard.salon.until')} ${untilLabel}` : ''}`;
    }
    if (repeatMode === 'weekly') {
      return `${t('pro_dashboard.salon.every_week')} le ${weekday} • ${timeLabel}${untilLabel ? ` • ${t('pro_dashboard.salon.until')} ${untilLabel}` : ''}`;
    }
    if (repeatMode === 'monthly') {
      return `${t('pro_dashboard.salon.every_month')} le ${dayNum} • ${timeLabel}${untilLabel ? ` • ${t('pro_dashboard.salon.until')} ${untilLabel}` : ''}`;
      return `Tous les mois le ${dayNum} • ${timeLabel}${untilLabel ? ` • jusqu’au ${untilLabel}` : ''}`;
    }
    if (repeatMode === 'yearly') {
      return `Tous les ans le ${dayNum} ${monthName} • ${timeLabel}`;
    }
    return '';
  }, [date, repeatMode, repeatUntil, fullDay, startTime, endTime]);

  const Wrapper = embedded ? Box : Container;
  const wrapperProps = embedded
    ? { sx: { width: '100%' } }
    : { maxWidth: 'lg', sx: { mt: 4, mb: 6 } };

  return (
    <Wrapper {...wrapperProps}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {t('pro_dashboard.salon.closures')}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
            {t('pro_dashboard.salon.create_closures_desc')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={!effectiveSalonId}
          sx={{ backgroundColor: theme.colors.secondary[500], textTransform: 'none', fontWeight: 700 }}
        >
          {t('pro_dashboard.salon.add')}
        </Button>
      </Box>

      {!effectiveSalonId && !isLoading && (
        <Alert severity="warning">{t('pro_dashboard.salon.no_salon_found')}</Alert>
      )}

      {salonsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des salons.
        </Alert>
      )}

      {closuresError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des fermetures.
        </Alert>
      )}

      <Grid container spacing={2}>
        {(Array.isArray(closures) ? closures : []).map((c) => (
          <Grid item xs={12} md={6} key={c.closure_id}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.colors.primary[100]}`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {formatDisplay(c, t)}
                    </Typography>
                    {c.reason ? (
                      <Typography variant="body2" sx={{ mt: 0.5, color: theme.colors.text.secondary }}>
                        {c.reason}
                      </Typography>
                    ) : null}

                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={c.is_recurring ? t('pro_dashboard.salon.annual') : t('pro_dashboard.salon.one_time')} />
                      <Chip
                        size="small"
                        label={c.status === 'active' ? t('pro_dashboard.salon.active') : t('pro_dashboard.salon.cancelled')}
                        color={c.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      onClick={() => openEdit(c)}
                      disabled={c.status !== 'active'}
                      aria-label="Modifier"
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(c.closure_id)}
                      disabled={deleting || c.status !== 'active'}
                      aria-label="Annuler"
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {!isLoading && Array.isArray(closures) && closures.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('pro_dashboard.salon.no_closure_recorded')}
        </Alert>
      )}

      <Dialog open={dialogOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? t('pro_dashboard.salon.edit_closure') : t('pro_dashboard.salon.new_closure')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {!isEditing ? (
              <>
                <FormControl>
                  <FormLabel>{t('pro_dashboard.salon.apply_to')}</FormLabel>
                  <RadioGroup
                    value={applyScope}
                    onChange={(e) => setApplyScope(e.target.value)}
                  >
                    <FormControlLabel value="single" control={<Radio />} label={t('pro_dashboard.salon.this_salon')} />
                    <FormControlLabel value="all" control={<Radio />} label={t('pro_dashboard.salon.all_my_salons')} />
                    <FormControlLabel value="list" control={<Radio />} label={t('pro_dashboard.salon.salon_selection')} />
                  </RadioGroup>
                </FormControl>

                {applyScope === 'list' && (
                  <FormControl fullWidth size="small">
                    <InputLabel id="closures-apply-salons-label">Salons</InputLabel>
                    <Select
                      labelId="closures-apply-salons-label"
                      multiple
                      value={applySalonIds}
                      label="Salons"
                      onChange={(e) => setApplySalonIds(e.target.value)}
                      renderValue={(selected) => {
                        const ids = Array.isArray(selected) ? selected : [];
                        const names = ids
                          .map((id) => salons.find((s) => Number(s.salon_id) === Number(id))?.name)
                          .filter(Boolean);
                        return names.join(', ');
                      }}
                    >
                      {salons.map((s) => (
                        <MenuItem key={s.salon_id} value={s.salon_id}>
                          <Checkbox checked={applySalonIds.includes(s.salon_id)} />
                          <ListItemText primary={s.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            ) : (
              <Alert severity="info">
                {t('pro_dashboard.salon.existing_closure_warning')}
              </Alert>
            )}

            <TextField
              label={t('pro_dashboard.salon.start')}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="closures-repeat-label">{t('pro_dashboard.salon.repeat')}</InputLabel>
              <Select
                labelId="closures-repeat-label"
                value={repeatMode}
                label={t('pro_dashboard.salon.repeat')}
                onChange={(e) => {
                  setRepeatMode(e.target.value);
                  if (e.target.value === 'once' || e.target.value === 'yearly') {
                    setRepeatUntil('');
                  }
                }}
              >
                <MenuItem value="once">{t('pro_dashboard.salon.once_only')}</MenuItem>
                <MenuItem value="daily" disabled={isEditing}>{t('pro_dashboard.salon.every_day')}</MenuItem>
                <MenuItem value="weekdays" disabled={isEditing}>{t('pro_dashboard.salon.every_weekday')}</MenuItem>
                <MenuItem value="weekly" disabled={isEditing}>{t('pro_dashboard.salon.every_week')}</MenuItem>
                <MenuItem value="monthly" disabled={isEditing}>{t('pro_dashboard.salon.every_month')}</MenuItem>
                <MenuItem value="yearly">{t('pro_dashboard.salon.yearly_repeat')}</MenuItem>
              </Select>
            </FormControl>

            {repeatMode !== 'once' && repeatMode !== 'yearly' && (
              <TextField
                label={t('pro_dashboard.salon.until')}
                type="date"
                value={repeatUntil}
                onChange={(e) => setRepeatUntil(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}

            <FormControlLabel
              sx={{ alignItems: 'flex-start' }}
              control={<Switch checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} />}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontWeight: 600 }}>{t('pro_dashboard.salon.full_day')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('pro_dashboard.salon.full_day_desc')}
                  </Typography>
                </Box>
              }
            />

            <Divider />

            {!fullDay && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={t('pro_dashboard.salon.start_time')}
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Heure fin"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            )}

            <Alert severity="info">{closureRecap}</Alert>

            <TextField
              label={t('pro_dashboard.salon.reason_optional')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            <Alert severity="warning">
              {t('pro_dashboard.salon.appointments_impacted')}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} sx={{ textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={isEditing ? updating : creating}
            sx={{ backgroundColor: theme.colors.secondary[500], textTransform: 'none', fontWeight: 700 }}
          >
            {isEditing ? t('pro_dashboard.salon.update') : t('pro_dashboard.salon.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={cancelDelete} maxWidth="xs">
        <DialogTitle>{t('pro_dashboard.salon.delete_closure_confirm')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('pro_dashboard.salon.delete_closure_warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} sx={{ textTransform: 'none' }}>
            {t('pro_dashboard.salon.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            disabled={deleting}
            sx={{ backgroundColor: theme.colors.secondary[500], textTransform: 'none', fontWeight: 700 }}
          >
            {deleting ? t('pro_dashboard.salon.deleting') : t('pro_dashboard.salon.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
};

export default SalonClosures;
